<template>
  <div class="table-container">
    <div class="header">
      <div class="title">{{ $t('subscribersAccounts') }} ({{ companies.length }})</div>
      <div class="form-container search">
        <div class="input-title">Recherche</div>
        <input v-model="search" type="text" class="input search" :placeholder="$t('companiesSearch')" />
      </div>
      <RouterLink class="main-button" to="/admin/company/add">
        {{ $t('addSubscriberAccount') }}
      </RouterLink>
    </div>

    <div class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <div class="table-header flex">
        <div v-for="(data, hIndex) in headers" :key="data + hIndex" class="table-header-item">
          {{ data }}
        </div>
      </div>

      <div class="table-content">
        <div v-for="(company, index) in companies" :key="company.id + index" class="companies" @click="() => selectCompany(company)">
          <div class="table-line" :class="{ visible: company.id === selectedCompany && !loading }">
            <div class="table-item chevron" :class="{ active: company.id === selectedCompany }">
              <div class="icon-chevron bottom" :class="company.id === selectedCompany ? 'blue' : 'grey'" />
            </div>
            <div class="table-item">
              <div class="item bold">{{ company.name }}</div>
            </div>
            <div class="table-item pl">
              <div class="item">{{ company.siren }}</div>
            </div>
            <div class="table-item full-line">
              <div class="item">
                <span class="link-button" @click="$router.push(`/admin/company/edit?companyId=${company.id}&companyName=${company.name}`)">{{
                  $t('modifyAccountName')
                }}</span>
              </div>
            </div>
          </div>

          <div
            class="dropdown-accounts"
            :class="{ visible: company.id === selectedCompany && !loading }"
            :style="{
              height: `${company.id === selectedCompany ? 90 * (users.length !== 0 ? users.length + 1 : 2) + 96 : 0}px`,
            }"
          >
            <div class="companies-header">
              <div class="form-container search">
                <input v-model="searchValue" type="text" class="input search" :placeholder="$t('usersSearch')" @click.stop />
              </div>
              <RouterLink class="main-button" :to="`/admin/account/add?companyId=${company.id}`">
                {{ $t('addAUser') }}
              </RouterLink>
            </div>
            <div class="table-dropdown-container">
              <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
                <thead class="table-header">
                  <tr>
                    <th v-for="data in subHeader" :key="data" class="table-header-item">
                      {{ data }}
                    </th>
                  </tr>
                </thead>

                <tbody v-if="!filteredUser.length" class="table-content">
                  <tr class="table-line">
                    <td class="table-item no-result">
                      {{ $t('noResults') }}
                    </td>
                  </tr>
                </tbody>

                <tbody v-else class="table-content">
                  <tr v-for="user in filteredUser" :key="user.id" class="table-line">
                    <td class="table-item">
                      <div class="item bold">{{ user.firstName }} {{ user.lastName }}</div>
                    </td>

                    <td class="table-item">
                      <div class="item">{{ user.email }}</div>
                    </td>

                    <td class="table-item">
                      <div class="item">
                        {{ user.isMainUser ? $t('mainUser') : $t('secondaryUser') }}
                      </div>
                    </td>

                    <td class="table-item">
                      <div class="item">
                        {{ $utils.formatDate(user.createdAt) }}
                      </div>
                    </td>

                    <td class="table-item">
                      <div class="item">
                        {{ !user.isActivated ? $utils.formatDate(user.updatedAt) : '-' }}
                      </div>
                    </td>

                    <td class="table-item">
                      <div class="item">
                        <span
                          class="link-button"
                          @click.stop="
                            $router.push(
                              `/admin/account/edit?companyId=${company.id}&email=${user.email}&firstName=${user.firstName}&lastName=${user.lastName}&userId=${user.id}`,
                            )
                          "
                          >{{ $t('change') }}</span
                        >
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!users.length" class="table-line empty">
                    <td class="table-item" colspan="5">
                      <div class="item">
                        <div v-if="loading" class="lds-ring-grey">
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                        {{ loading ? $t('searchInProgress') : $t('noUser') }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from '@/helpers/debounce';

export default {
  data() {
    return {
      selectedCompany: -1,
      headers: ['', this.$t('companyLabel'), this.$t('SIREN'), ' '],
      searchValue: '',
      companies: [],
      users: [],
      subHeader: [this.$t('fullName'), this.$t('email'), this.$t('type'), this.$t('creation'), this.$t('disable'), ''],
      loading: true,
      searchDebounce: () => null,
      debounce,
      search: '',
    };
  },

  computed: {
    filteredUser() {
      const users = this.users;
      if (!this.searchValue) return users;

      return users.filter(x =>
        this.searchValue.split(' ').every(word => {
          const regex = new RegExp(word.toLowerCase());
          return (
            regex.test(x.firstName.toLowerCase()) ||
            regex.test(x.lastName.toLowerCase()) ||
            regex.test(x.email.toLowerCase()) ||
            regex.test(x.isMainUser ? this.$t('mainUser').toLowerCase() : this.$t('secondaryUser').toLowerCase())
          );
        }),
      );
    },
  },

  watch: {
    search() {
      this.searchDebounce();
    },
  },

  async mounted() {
    this.loading = true;
    this.companies = await this.$store.dispatch('getClients', this.search);
    this.loading = false;
  },

  async created() {
    this.searchDebounce = this.debounce(this.getClients, 300);
  },

  methods: {
    async selectCompany(company) {
      this.searchValue = '';
      this.loading = true;
      this.selectedCompany = this.selectedCompany === company.id ? -1 : company.id;
      this.users = (await this.$store.dispatch('getUsers', { id: company.id })).users;
      this.loading = false;
    },

    async getClients() {
      this.companies = await this.$store.dispatch('getClients', this.search);
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.input {
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  box-sizing: border-box;
  color: colors.$text-color;
  border: solid 1px colors.$lightgrey-color;
  border-radius: 4px;
  background: colors.$white-color;

  &::placeholder {
    color: colors.$lightgrey-color;
  }

  &.incorrect {
    border-color: colors.$error-color;
  }
}

.form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  width: 30%;

  .input-title {
    width: 100%;
    color: colors.$darkgrey-color;
    font-weight: 500;
    font-size: 16px;
    margin-bottom: 10px;
  }
}

.table-container {
  .header {
    margin: 40px 0 0;
    display: flex;
    align-items: flex-start;
    width: 100%;
    justify-content: space-between;

    .title {
      display: flex;
      align-items: flex-end;
      font-weight: 500;
      font-size: 20px;
    }
    .main-button {
      width: 240px;
      padding: 0;
    }
  }
}

.table-line.visible {
  background: #f1f1f1ab !important;
}

.table {
  border-bottom: none;
}

.table .table-header,
.table .table-content {
  &.flex {
    display: flex;

    .table-header-item {
      width: 20%;
      display: flex;
      align-items: center;
      justify-content: center;

      &:first-child {
        width: 0px;
      }

      &:last-child {
        flex-grow: 1;
      }
    }
  }

  &.table-content {
    flex-direction: column;

    .companies .dropdown-accounts {
      height: 0;
      overflow: hidden;
      transition: all 0.2s;
      opacity: 0;
      box-sizing: border-box;

      .companies-header {
        margin: 24px 24px;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
      }

      &.visible {
        opacity: 1;
        border-bottom: 1px solid colors.$border-color;
        background: #f1f1f1ab;
      }

      .table-dropdown-container {
        display: flex;
        border: 1px solid colors.$border-color;
        border-radius: 8px;
        overflow: hidden;
        width: calc(100% - 48px);
        margin: 24px;

        .table {
          flex-grow: 1;
          margin: 0;
          padding: 0;
          border: none;
          border-collapse: collapse;

          .table-header {
            tr th {
              text-align: center;
            }
          }
          .table-content {
            box-sizing: border-box;

            tr {
              display: table-row;

              td {
                display: table-cell;
                width: auto;
              }
            }
          }
        }
      }

      .table-line {
        &:hover {
          background: colors.$white-color;
          cursor: initial;
        }
      }
    }
  }
}

.table .table-content .table-line {
  display: flex;
  width: 100%;
  background: colors.$white-color;
  border-bottom: 1px solid colors.$border-color;

  &:hover {
    background: rgba(colors.$lightgrey-color, 0.1);
    cursor: pointer;
  }

  &:last-child .table-item {
    border: none;
  }

  .table-item {
    width: 20%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    position: relative;
    border-bottom: none;

    &.pl {
      padding-left: 75px;
    }

    &.chevron {
      width: 48px;

      &.active .icon-chevron {
        transition: all 0.5s;
        transform: rotate(135deg);
      }
    }

    &.no-result {
      width: 370%;
      display: flex;
      justify-content: center;
      align-items: center;

      background: white !important;
    }

    &.full-line {
      flex-grow: 1;
      .item {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding-right: 16px;
      }
    }

    .item {
      width: 100%;
      color: colors.$darkgrey-color;
      font-size: 13px;
      font-weight: normal;
      display: flex;
      justify-content: center;
      &.bold {
        font-weight: 500;
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      height: 192px;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;

        .line-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          .item {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
        }
      }
    }
  }
}
</style>
